import { useCallback, useEffect, useState } from 'react';

/**
 * MERCURY_ADMIN_COMMAND — the back office, at /admin/command.
 *
 * Three panels, because there were three questions nobody could answer without
 * opening the database by hand: what have people reported, who is signed in,
 * and is our course data any good.
 *
 * The course panel is the one that earns its keep. Sanctuary Cove alone was
 * hiding two faults — The Palms missing holes 9 and 10 because they sit outside
 * the course boundary in OpenStreetMap, and The Pines carrying no green
 * outlines because it had not rebuilt. Neither is visible from the app; both
 * are obvious here.
 *
 * The admin key is held in memory for the session only. It is never written to
 * localStorage: a shared laptop in a pro shop is exactly where a key left in
 * storage gets found.
 */

const API = '/api/admin';

interface Ticket {
  id: string; kind: string; status: string; courseId: string | null;
  holeNumber: number | null; appVersion: string | null; createdAt: number;
  message?: string; contact?: string;
}
interface Account {
  id: string; email: string | null; display_name: string | null;
  created_at: number; last_seen_at: number | null;
  live_sessions: number; providers: string | null;
}
interface CourseHealth {
  id: string; name: string; holes: number; builderV: number | null;
  withCentreline: number; withGreenRing: number; hazards: number;
  hazardsWithRing: number; duplicateHoles: number[]; missingHoles: number[];
  updatedAt: number | null;
}

type Panel = 'tickets' | 'accounts' | 'courses' | 'map';

const when = (ms: number | null) =>
  ms ? new Date(ms).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' }) : '–';

export default function AdminSuite() {
  const [key, setKey] = useState('');
  const [authed, setAuthed] = useState(false);
  const [panel, setPanel] = useState<Panel>('tickets');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [courses, setCourses] = useState<CourseHealth[]>([]);
  const [showClosed, setShowClosed] = useState(false);
  const [mapCourse, setMapCourse] = useState('');
  // /map reads the chosen course from its own settings, so the id is passed
  // through the query string it already understands.
  const mapHref = mapCourse ? `/map?course=${encodeURIComponent(mapCourse)}` : '/map';

  const call = useCallback(async (pathname: string, init?: RequestInit) => {
    const res = await fetch(API + pathname, {
      ...init,
      headers: { 'content-type': 'application/json', 'x-fairway-admin': key, ...(init?.headers ?? {}) },
    });
    if (res.status === 401) throw new Error('That key was not accepted.');
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
    return res.json();
  }, [key]);

  const refresh = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      if (panel === 'map') { setAuthed(true); return; }
      if (panel === 'tickets') {
        setTickets((await call(`/tickets?status=${showClosed ? 'all' : 'open'}`)).tickets ?? []);
      } else if (panel === 'accounts') {
        setAccounts((await call('/accounts')).accounts ?? []);
      } else {
        setCourses((await call('/courses')).courses ?? []);
      }
      setAuthed(true);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [call, panel, showClosed]);

  useEffect(() => { if (authed) void refresh(); }, [authed, panel, showClosed, refresh]);

  if (!authed) {
    return (
      <div className="adm-gate">
        <form
          className="adm-card"
          onSubmit={(e) => { e.preventDefault(); void refresh(); }}
        >
          <h1>MERCURY_ADMIN_COMMAND</h1>
          <p className="adm-muted">
            Support reports contain what golfers wrote about themselves. They are encrypted
            at rest and decrypted only for this screen.
          </p>
          <input
            type="password" value={key} onChange={(e) => setKey(e.target.value)}
            placeholder="Admin key" autoFocus autoComplete="off" spellCheck={false}
          />
          <button className="adm-primary" disabled={!key || busy}>
            {busy ? 'Checking…' : 'Unlock'}
          </button>
          {error && <p className="adm-error">{error}</p>}
        </form>
      </div>
    );
  }

  return (
    <div className="adm">
      <header className="adm-top">
        <b>MERCURY_ADMIN_COMMAND</b>
        <nav>
          {(['tickets', 'accounts', 'courses', 'map'] as Panel[]).map((p) => (
            <button key={p} className={panel === p ? 'on' : ''} onClick={() => setPanel(p)}>
              {p === 'tickets' ? 'Reports' : p === 'accounts' ? 'Golfers'
                : p === 'courses' ? 'Course data' : 'Map'}
            </button>
          ))}
        </nav>
        <button className="adm-ghost" onClick={() => void refresh()} disabled={busy}>
          {busy ? '…' : 'Refresh'}
        </button>
      </header>

      {error && <p className="adm-error">{error}</p>}

      {panel === 'tickets' && (
        <section>
          <label className="adm-check">
            <input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
            include closed
          </label>
          {!tickets.length && <p className="adm-muted">Nothing reported.</p>}
          {tickets.map((t) => (
            <article key={t.id} className={`adm-ticket ${t.status}`}>
              <div className="adm-ticket-head">
                <span className="adm-kind">{t.kind}</span>
                {t.courseId && <span className="adm-muted">{t.courseId}{t.holeNumber ? ` · hole ${t.holeNumber}` : ''}</span>}
                <span className="adm-muted">{when(t.createdAt)}</span>
                {t.status === 'open' && (
                  <button
                    className="adm-ghost"
                    onClick={async () => {
                      await call('/tickets/close', { method: 'POST', body: JSON.stringify({ id: t.id }) });
                      void refresh();
                    }}
                  >Close</button>
                )}
              </div>
              <p className="adm-msg">{t.message}</p>
              {t.contact && <p className="adm-muted">reply to {t.contact}</p>}
            </article>
          ))}
        </section>
      )}

      {panel === 'accounts' && (
        <section>
          <table className="adm-table">
            <thead><tr><th>Golfer</th><th>Sign-in</th><th>Sessions</th><th>Last seen</th><th /></tr></thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.id}>
                  <td>{a.display_name || a.email || a.id}</td>
                  <td className="adm-muted">{a.providers ?? '–'}</td>
                  <td>{a.live_sessions}</td>
                  <td className="adm-muted">{when(a.last_seen_at)}</td>
                  <td>
                    {/* The control that matters on a lost phone. */}
                    <button
                      className="adm-ghost"
                      disabled={!a.live_sessions}
                      onClick={async () => {
                        await call('/accounts/revoke', { method: 'POST', body: JSON.stringify({ userId: a.id }) });
                        void refresh();
                      }}
                    >Sign out everywhere</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!accounts.length && <p className="adm-muted">No accounts yet.</p>}
        </section>
      )}

      {panel === 'map' && (
        <section className="adm-map">
          {/*
            The full head-unit map, with every tool it already has — pan, zoom,
            compass, hole stepper, park, lock. Embedded rather than rebuilt:
            /map is the same surface the buggy runs, so the console can never
            drift from what the customer actually sees, and there is no second
            copy of the map to keep in step.
          */}
          <div className="adm-map-bar">
            <label>
              Course
              <select value={mapCourse} onChange={(e) => setMapCourse(e.target.value)}>
                <option value="">— pick a course —</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>{c.name} · {c.holes} holes</option>
                ))}
              </select>
            </label>
            <a className="adm-ghost" href={mapHref} target="_blank" rel="noreferrer">Open full screen</a>
          </div>
          {courses.length === 0 && <p className="adm-muted">Open “Course data” once to load the list.</p>}
          <iframe className="adm-map-frame" src={mapHref} title="Course map" />
        </section>
      )}

      {panel === 'courses' && (
        <section>
          <p className="adm-muted">
            {courses.length} courses built · {courses.filter((c) => c.withGreenRing > 0).length} carry
            mapped outlines · {courses.filter((c) => c.missingHoles.length || c.duplicateHoles.length).length} need attention
          </p>
          <table className="adm-table">
            <thead>
              <tr><th>Course</th><th>Holes</th><th>Outlines</th><th>Hazards</th><th>Problems</th><th>Built</th></tr>
            </thead>
            <tbody>
              {courses.map((c) => {
                const bad = c.missingHoles.length || c.duplicateHoles.length;
                return (
                  <tr key={c.id} className={bad ? 'adm-bad' : ''}>
                    <td>{c.name}<br /><span className="adm-muted">{c.id}</span></td>
                    <td>{c.holes}</td>
                    {/* Outlines are the HD story: greens with a real shape, not a circle. */}
                    <td>{c.withGreenRing}/{c.holes}</td>
                    <td>{c.hazardsWithRing}/{c.hazards}</td>
                    <td>
                      {c.duplicateHoles.length > 0 && <div>duplicate: {c.duplicateHoles.join(', ')}</div>}
                      {c.missingHoles.length > 0 && <div>missing: {c.missingHoles.join(', ')}</div>}
                      {!bad && <span className="adm-muted">–</span>}
                    </td>
                    <td className="adm-muted">v{c.builderV ?? '?'}<br />{when(c.updatedAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
